import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Footer from "../shared/Footer";
import Navbar from "../shared/Navbar";
import Sidebar from "../shared/Sidebar";
import Swal from "sweetalert2";
import config from "../../config";

function SpecialExamSchedule() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem("intermediateAdminToken");

  const [loading, setLoading] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [isFinalSaved, setIsFinalSaved] = useState(false);
  const [streams, setStreams] = useState([]);
  const [selectedStream, setSelectedStream] = useState("");
  const [isSearched, setIsSearched] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [streamStatus, setStreamStatus] = useState({});
  const [showFinalSave, setShowFinalSave] = useState(false);
  const [saveTypeStatus, setSaveTypeStatus] = useState("NO");

  const getCleanStream = (raw) => {
    const lower = raw.toString().toLowerCase().trim();
    if (lower.includes("sci")) return "SCIENCE";
    if (lower.includes("com")) return "COMMERCE";
    if (lower.includes("art")) return "ARTS";
    return raw.toString().toUpperCase().trim();
  };

  const parseExamTimeStatus = (apiData, rawSubjects = []) => {
    let loadedTimeSlots = [];
    const timeStatusSrc = apiData?.data?.examTimeStatus || apiData?.examTimeStatus;
    if (timeStatusSrc && Array.isArray(timeStatusSrc)) {
      loadedTimeSlots = timeStatusSrc.map((slot) =>
        (typeof slot === "string" ? slot : slot.exaM_TIME || slot.time || slot.examTime || "").trim()
      );
    } else {
      const slotsSrc = apiData?.data?.timeSlots || apiData?.timeSlots;
      if (slotsSrc && Array.isArray(slotsSrc)) {
        loadedTimeSlots = slotsSrc.map((slot) =>
          (typeof slot === "string" ? slot : slot.exaM_TIME || slot.time || slot.examTime || "").trim()
        );
      }
    }
    const subjectTimes = (rawSubjects || []).map((sub) => (sub.time || sub.examTime || sub.exaM_TIME || "").trim()).filter(Boolean);
    return Array.from(new Set([...loadedTimeSlots, ...subjectTimes].filter(Boolean)));
  };

  const filteredSubjects = subjects.filter(
    (sub) =>
      (sub.subjectName || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (sub.subjectCode || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (sub.subjectType || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      String(sub.eligibleStudentCount || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (sub.examDate || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (sub.examTime || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    if (!token) {
      navigate("/intermidate-admin-login");
    } else {
      fetchPageLoadData();
    }
  }, [token]);

  useEffect(() => {
    if (isSearched && selectedStream && subjects && subjects.length > 0) {
      const allStreamsCompleted = ["ARTS", "SCIENCE", "COMMERCE"].every(
        (st) => streamStatus[st] === "COMPLETED"
      );
      setShowFinalSave(allStreamsCompleted || saveTypeStatus === "YES");
    } else {
      setShowFinalSave(false);
    }
  }, [subjects, isSearched, selectedStream, streamStatus, saveTypeStatus]);

  const fetchPageLoadData = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${config.API_BASE_URL2}/CSExamPageLoadStreamExaminationSchedule_CL12`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      if (response.ok) {
        // Parse stream list from API response if present
        let loadedStreams = [];
        const streamSrc = data.data?.pageloadStream_list;
        if (streamSrc && Array.isArray(streamSrc)) {
          loadedStreams = streamSrc.map((item) => {
            if (typeof item === "string") return item;
            if (typeof item === "object" && item !== null) {
              return item.streams;
            }
            return "";
          }).filter(Boolean);
        }

        // Handle variations in structure for subjects
        let loadedSubjects = [];
        if (data.data) {
          if (Array.isArray(data.data)) {
            loadedSubjects = data.data;
          } else if (Array.isArray(data.data.examinationScheduleDetails)) {
            loadedSubjects = data.data.examinationScheduleDetails;
          } else if (Array.isArray(data.data.examinationSchedule_list)) {
            loadedSubjects = data.data.examinationSchedule_list;
          }
        } else if (Array.isArray(data)) {
          loadedSubjects = data;
        }

        // If stream list not explicitly returned, extract from subjects stream properties
        if (loadedStreams.length === 0 && loadedSubjects.length > 0) {
          const uniqueStreams = new Set(loadedSubjects.map(sub => {
            const rawStream = sub.stream || sub.streams || sub.subjecT_STREAM || sub.facultY || sub.faculty || "";
            return getCleanStream(rawStream);
          }).filter(Boolean));
          loadedStreams = Array.from(uniqueStreams);
        }

        if (loadedStreams.length === 0) {
          loadedStreams = ["SCIENCE", "COMMERCE", "ARTS"];
        }
        setStreams(loadedStreams);

        // Parse time slots / shifts from API
        const activeTimeSlots = parseExamTimeStatus(data, loadedSubjects);
        setTimeSlots(activeTimeSlots);

        const formattedSubjectsForCheck = loadedSubjects.map((sub) => {
          const rawStream = sub.stream || sub.streams || sub.subjecT_STREAM || sub.facultY || sub.faculty || "";
          const rawTime = (sub.time || sub.examTime || sub.exaM_TIME || "").trim();
          const cleanStream = getCleanStream(rawStream);
          const isValidTime = activeTimeSlots.length === 0 || !rawTime || activeTimeSlots.includes(rawTime);
          const allowedTime = isValidTime ? rawTime : "";
          return {
            subjectCode: sub.subjecT_ID || sub.subjectCode || sub.subjecT_CODE || "",
            subjectName: sub.subjecT_NAME || sub.subjectName || "",
            subjectType: sub.subjecT_TYPE || sub.subjectType || sub.type || sub.subject_type || sub.subjecT_CAT || sub.subjectCategory || "COMPULSORY",
            eligibleStudentCount: sub.eligiblE_CANDIDATE !== undefined ? sub.eligiblE_CANDIDATE : (sub.eligibleStudentCount || sub.eligiblE_STUDENT_COUNT || 0),
            examDate: sub.date || sub.examDate || sub.exaM_DATE || "",
            examTime: allowedTime,
            stream: cleanStream,
          };
        });

        const hnbSubject = formattedSubjectsForCheck.find(
          (sub) => sub.subjectType === "CORE" && sub.subjectCode === "HNB"
        );
        if (hnbSubject) {
          formattedSubjectsForCheck.forEach((sub) => {
            if (sub.subjectType === "MATRI_BHASA") {
              sub.examDate = sub.examDate || hnbSubject.examDate;
              sub.examTime = sub.examTime || hnbSubject.examTime;
            }
          });
        }

        parseAndSetStreamStatus(data, formattedSubjectsForCheck);
      } else {
        Swal.fire("Error", data.message || "Failed to load stream details.", "error");
      }
    } catch (error) {
      console.error("Error loading special exam schedule data:", error);
      Swal.fire("Error", "Failed to load special exam schedule data.", "error");
    } finally {
      setLoading(false);
    }
  };

  const parseAndSetStreamStatus = (apiData, subjectsList) => {
    let isFinal = false;
    if (apiData?.data?.typeStatus?.savE_TYPE_STATUS === "YES" || apiData?.typeStatus?.savE_TYPE_STATUS === "YES") {
      isFinal = true;
    } else if (apiData?.data) {
      if (
        apiData.data.isFinalSubmit === true ||
        apiData.data.isFinalSave === true ||
        apiData.data.isFinalSaved === true ||
        apiData.data.status === "FINAL" ||
        apiData.data.saveType === "FINAL" ||
        apiData.data.finaL_SAVE === "YES" ||
        apiData.data.finaL_SAVE_STATUS === "YES" ||
        apiData.data.finalSaveStatus === "YES" ||
        apiData.data.finaL_SUBMIT === "YES" ||
        apiData.data.savE_TYPE_STATUS === "YES" ||
        String(apiData.data.isFinalSave).toUpperCase() === "YES" ||
        String(apiData.data.isFinalSaved).toUpperCase() === "YES" ||
        String(apiData.data.isFinalSubmit).toUpperCase() === "YES"
      ) {
        isFinal = true;
      }
    }
    if (!isFinal && apiData) {
      if (
        apiData.finaL_SAVE === "YES" ||
        apiData.finaL_SAVE_STATUS === "YES" ||
        apiData.finalSaveStatus === "YES" ||
        apiData.finaL_SUBMIT === "YES" ||
        apiData.savE_TYPE_STATUS === "YES" ||
        String(apiData.isFinalSave).toUpperCase() === "YES" ||
        String(apiData.isFinalSaved).toUpperCase() === "YES" ||
        String(apiData.isFinalSubmit).toUpperCase() === "YES" ||
        apiData.status === "FINAL" ||
        apiData.saveType === "FINAL" ||
        apiData.isFinalSubmit === true ||
        apiData.isFinalSave === true ||
        apiData.isFinalSaved === true
      ) {
        isFinal = true;
      }
    }
    setIsFinalSaved(isFinal);

    let currentSaveTypeStatus = "NO";
    const rawSaveType =
      apiData?.data?.typeStatus?.savE_TYPE_STATUS ||
      apiData?.typeStatus?.savE_TYPE_STATUS ||
      apiData?.data?.savE_TYPE_STATUS ||
      apiData?.savE_TYPE_STATUS ||
      "";
    if (String(rawSaveType).toUpperCase().trim() === "YES") {
      currentSaveTypeStatus = "YES";
    }
    setSaveTypeStatus(currentSaveTypeStatus);

    const checkStreams = ["ARTS", "SCIENCE", "COMMERCE"];
    let statusSrc = null;
    if (Array.isArray(apiData?.data?.streamStatus)) {
      statusSrc = apiData.data.streamStatus;
    } else if (Array.isArray(apiData?.streamStatus)) {
      statusSrc = apiData.streamStatus;
    } else if (Array.isArray(apiData?.data?.pageloadStream_list)) {
      statusSrc = apiData.data.pageloadStream_list;
    } else if (Array.isArray(apiData?.pageloadStream_list)) {
      statusSrc = apiData.pageloadStream_list;
    } else if (Array.isArray(apiData?.data?.stream_list)) {
      statusSrc = apiData.data.stream_list;
    }

    setStreamStatus((prevStatus) => {
      const statusMap = { ...prevStatus };

      checkStreams.forEach((st) => {
        let match = null;
        if (Array.isArray(statusSrc)) {
          match = statusSrc.find((item) => {
            if (typeof item === "string") return getCleanStream(item) === st;
            if (typeof item === "object" && item !== null) {
              const name =
                item.streaM_NAME ||
                item.streamName ||
                item.streaM ||
                item.stream ||
                item.streams ||
                item.facultY ||
                item.faculty ||
                item.name ||
                "";
              return getCleanStream(name) === st;
            }
            return false;
          });
        }

        if (match && typeof match === "object") {
          const rawStatus = String(
            match.status ||
            match.streaM_STATUS ||
            match.streamStatus ||
            match.savE_TYPE_STATUS ||
            match.finaL_SAVE ||
            ""
          )
            .toUpperCase()
            .trim();

          if (
            rawStatus === "YES" ||
            rawStatus === "COMPLETED" ||
            rawStatus === "COMPLETE" ||
            rawStatus === "FINAL" ||
            match.isCompleted === true ||
            match.finaL_SAVE === "YES" ||
            match.finaL_SAVE_STATUS === "YES"
          ) {
            statusMap[st] = "COMPLETED";
          } else if (
            rawStatus === "DRAFT" ||
            rawStatus === "SAVED" ||
            rawStatus === "DRAFT_SAVED" ||
            rawStatus === "DRAFTED"
          ) {
            statusMap[st] = "DRAFT";
          } else {
            statusMap[st] = "NOT_COMPLETED";
          }
        } else if (!statusMap[st]) {
          statusMap[st] = "NOT_COMPLETED";
        }
      });

      return statusMap;
    });
  };

  const handleSearchStream = async () => {
    if (!selectedStream) {
      Swal.fire("Stream Required", "Please select a stream first.", "warning");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${config.API_BASE_URL2}/CSExamFetchExaminationSchedule_CL12`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            streams: selectedStream,
          }),
        }
      );

      const data = await response.json();
      if (response.ok) {
        let loadedSubjects = [];
        if (data.data) {
          if (Array.isArray(data.data)) {
            loadedSubjects = data.data;
          } else if (Array.isArray(data.data.examinationScheduleDetails)) {
            loadedSubjects = data.data.examinationScheduleDetails;
          } else if (Array.isArray(data.data.examinationSchedule_list)) {
            loadedSubjects = data.data.examinationSchedule_list;
          }
        } else if (Array.isArray(data)) {
          loadedSubjects = data;
        }

        // Parse time slots / shifts from API response
        const activeTimeSlots = parseExamTimeStatus(data);
        setTimeSlots(activeTimeSlots);

        let hasInvalidTime = false;
        const formattedSubjects = loadedSubjects.map((sub) => {
          const rawStream = sub.stream || sub.streams || sub.subjecT_STREAM || sub.facultY || sub.faculty || "";
          const rawTime = (sub.time || sub.examTime || sub.exaM_TIME || "").trim();
          const cleanStream = getCleanStream(rawStream);
          const isValidTime = activeTimeSlots.length === 0 || !rawTime || activeTimeSlots.includes(rawTime);
          if (cleanStream === selectedStream || cleanStream === "ALL") {
            if (rawTime && !isValidTime) {
              hasInvalidTime = true;
            }
          }
          const allowedTime = isValidTime ? rawTime : "";
          return {
            subjectCode: sub.subjecT_ID || sub.subjectCode || sub.subjecT_CODE || "",
            subjectName: sub.subjecT_NAME || sub.subjectName || "",
            subjectType: sub.subjecT_TYPE || sub.subjectType || sub.type || sub.subject_type || sub.subjecT_CAT || sub.subjectCategory || "COMPULSORY",
            eligibleStudentCount: sub.eligiblE_CANDIDATE !== undefined ? sub.eligiblE_CANDIDATE : (sub.eligibleStudentCount || sub.eligiblE_STUDENT_COUNT || 0),
            examDate: sub.date || sub.examDate || sub.exaM_DATE || "",
            examTime: allowedTime,
            stream: cleanStream,
          };
        });

        // Filter on frontend too in case the API returned everything
        const streamFiltered = formattedSubjects.filter(
          (sub) => sub.stream === selectedStream || sub.stream === "ALL"
        );

        // Sync HNB date & time to MATRI_BHASA subjects if HNB has them
        const hnbSubject = streamFiltered.find(
          (sub) => sub.subjectType === "CORE" && sub.subjectCode === "HNB"
        );
        if (hnbSubject) {
          streamFiltered.forEach((sub) => {
            if (sub.subjectType === "MATRI_BHASA") {
              sub.examDate = hnbSubject.examDate;
              sub.examTime = hnbSubject.examTime;
            }
          });
        }

        setSubjects(streamFiltered);
        setIsSearched(true);

        parseAndSetStreamStatus(data, streamFiltered);

        if (hasInvalidTime) {
          Swal.fire(
            "Warning",
            `Some subjects have exam times other than those allowed by the API (${activeTimeSlots.join(", ")}). These invalid times have been cleared and will not be saved.`,
            "warning"
          );
        }
      } else {
        Swal.fire("Error", data.message || "Failed to fetch stream subjects.", "error");
      }
    } catch (error) {
      console.error("Error loading stream subjects:", error);
      Swal.fire("Error", "Failed to fetch stream subjects.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (index, dateString) => {
    const updated = [...subjects];
    updated[index].examDate = dateString;
    const changedSub = updated[index];
    if (changedSub.subjectType === "CORE" && changedSub.subjectCode === "HNB") {
      updated.forEach((sub) => {
        if (sub.subjectType === "MATRI_BHASA") {
          sub.examDate = dateString;
        }
      });
    }
    setSubjects(updated);
  };

  const handleTimeChange = (index, timeString) => {
    const updated = [...subjects];
    updated[index].examTime = timeString;
    const changedSub = updated[index];
    if (changedSub.subjectType === "CORE" && changedSub.subjectCode === "HNB") {
      updated.forEach((sub) => {
        if (sub.subjectType === "MATRI_BHASA") {
          sub.examTime = timeString;
        }
      });
    }
    setSubjects(updated);
  };

  const handleSave = async (saveType) => {
    // Check row-level dependency: if either date or time is entered, both must be provided
    const invalidRow = subjects.find(
      (sub) => (sub.examDate && !sub.examTime) || (!sub.examDate && sub.examTime)
    );
    if (invalidRow) {
      Swal.fire(
        "Incomplete Row Details",
        `For subject "${invalidRow.subjectName}", please select both Exam Date and Time (Shift).`,
        "warning"
      );
      return;
    }

    // If Final Save, check that all date and time slots are filled
    // if (saveType === "FINAL") {
    //   const incomplete = subjects.some((sub) => !sub.examDate || !sub.examTime);
    //   if (incomplete) {
    //     Swal.fire(
    //       "Required Fields Missing",
    //       "Please select Exam Date and Time for all subjects before Final Save.",
    //       "warning"
    //     );
    //     return;
    //   }
    // }

    // Confirmation Prompts for Draft & Final Save
    const confirmTitle = saveType === "FINAL" ? "Final Save?" : "Draft Save?";
    const confirmText =
      saveType === "FINAL"
        ? "Do you really want to make this a final save? Once finalized, you cannot make any further edits."
        : "Do you really want to save this schedule as draft?";
    const confirmButtonText = saveType === "FINAL" ? "Yes, Final Save!" : "Yes, Save Draft!";

    const confirmResult = await Swal.fire({
      title: confirmTitle,
      text: confirmText,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: confirmButtonText,
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    setLoading(true);
    try {
      let url = "";
      let options = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      if (saveType === "DRAFT") {
        url = `${config.API_BASE_URL2}/CSExamExaminationScheduleInsertDraft_CL12`;
        options.method = "POST";
        options.body = JSON.stringify({
          DraftInsertSubjectList: subjects.map((sub) => {
            const rawTime = (sub.examTime || "").trim();
            const allowedTime = (timeSlots && timeSlots.length > 0)
              ? (timeSlots.includes(rawTime) ? rawTime : rawTime || null)
              : (rawTime || null);
            return {
              streams: sub.stream || "ALL",
              suB_CODE: sub.subjectCode,
              suB_NAME: sub.subjectName,
              date: sub.examDate || null,
              time: allowedTime,
            };
          }),
        });
      } else {
        url = `${config.API_BASE_URL2}/CSExamExaminationScheduleFinalSave_CL12`;
        options.method = "GET";
      }

      const response = await fetch(url, options);

      const data = await response.json();
      if (response.ok) {
        Swal.fire(
          "Success",
          `Special Exam Schedule saved as ${saveType.toLowerCase()} successfully!`,
          "success"
        );
        if (saveType === "FINAL") {
          setIsFinalSaved(true);
        }
        await handleSearchStream(); // reload fresh data for stream
        // await fetchPageLoadData(); 
      } else {
        Swal.fire(
          "Error",
          data.message || "Failed to save exam schedule.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error saving special exam schedule:", error);
      Swal.fire(
        "Error",
        "Failed to save special exam schedule.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    Swal.fire({
      title: "Reset Schedule?",
      text: "Are you sure you want to reset all date and time inputs to their last saved state?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, reset it!"
    }).then((result) => {
      if (result.isConfirmed) {
        handleSearchStream();
      }
    });
  };

  return (
    <>
      {loading && (
        <div className="custom-loader-container">
          <div className="custom-loader"></div>
        </div>
      )}
      <div className="container-scroller">
        <Navbar />
        <div className="container-fluid page-body-wrapper">
          <Sidebar />
          <div className="main-panel">
            <div className="content-wrapper">
              <div className="row">
                <div className="col-md-12 mb-2">
                  <section className="content-header">
                    <h1>
                      <i className="fa fa-calendar" />
                      &nbsp;Special Exam Schedule
                    </h1>
                    <ol className="breadcrumb">
                      <li>
                        <a
                          onClick={() => navigate("/intermediate-admin-dashboard")}
                          style={{ cursor: "pointer" }}
                        >
                          <i
                            className="fa fa-tachometer"
                            style={{ marginRight: "5px" }}
                          ></i>{" "}
                          Home
                        </a>
                      </li>
                      <li className="active">Special Exam Schedule</li>
                    </ol>
                  </section>
                </div>
              </div>

              <div className="row">
                <div className="col-md-12 mb-3">
                  <div className="card">
                    <div className="card-header bg-green">
                      <h5 className="mb-0 p-2 text-white">Manage Special Exam Schedule</h5>
                    </div>
                    <div className="card-body">
                      {/* Stream Dropdown Option */}
                      <div className="row align-items-end mb-4">
                        <div className="col-md-2">
                          <label className="form-label font-weight-bold">Stream <span className="text-danger">*</span></label>
                          <select
                            className="form-select form-select-sm"
                            value={selectedStream}
                            onChange={(e) => setSelectedStream(e.target.value)}
                            disabled={loading}
                          >
                            <option value="">-- Select Stream --</option>
                            {streams.map((st) => (
                              <option key={st} value={st}>
                                {st}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-1">
                          <button
                            type="button"
                            className="btn btn-success"
                            onClick={handleSearchStream}
                            disabled={loading || !selectedStream}
                            style={{ height: "40px" }}
                          >
                            <i className="fa fa-search"></i>&nbsp;Search
                          </button>
                        </div>
                        {isSearched && (
                          <>
                            <div className="col-md-7 d-flex align-items-center flex-wrap gap-2 mt-4">
                              {["ARTS", "COMMERCE", "SCIENCE"].map((st) => {
                                const status = streamStatus[st] || "NOT_COMPLETED";
                                let bg = "#f8f9fa";
                                let border = "#dee2e6";
                                let text = "#6c757d";
                                let iconClass = "fa-clock-o text-secondary";
                                let label = "Not Completed";

                                if (status === "COMPLETED") {
                                  bg = "#d1e7dd";
                                  border = "#198754";
                                  text = "#0f5132";
                                  iconClass = "fa-check-circle text-success";
                                  label = "Completed";
                                } else if (status === "DRAFT") {
                                  bg = "#fff3cd";
                                  border = "#ffc107";
                                  text = "#664d03";
                                  iconClass = "fa-pencil-square-o text-warning";
                                  label = "Draft Saved";
                                }

                                return (
                                  <>
                                    <div
                                      key={st}
                                      className="px-3 py-2 rounded border d-inline-flex align-items-center me-2 shadow-sm"
                                      style={{
                                        backgroundColor: bg,
                                        borderColor: border,
                                        color: text,
                                        fontSize: "13px",
                                        fontWeight: "600",
                                        height: "40px",
                                      }}
                                    >
                                      <i
                                        className={`fa ${iconClass}`}
                                        style={{ marginRight: "6px", fontSize: "15px" }}
                                      ></i>
                                      <span>{st}: {label}</span>
                                    </div>

                                  </>
                                );

                              })}
                            </div>
                            <div className="col-md-2 mt-4 d-flex justify-content-end">
                              {showFinalSave && (
                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  onClick={() => handleSave("FINAL")}
                                  disabled={loading || isFinalSaved}
                                >
                                  <i className="fa fa-check"></i>&nbsp;Final Save
                                </button>
                              )}
                            </div>                          </>
                        )}
                      </div>

                      {isSearched && (
                        <>
                          {/* Search option for subjects inside the selected stream */}
                          <div className="d-flex justify-content-end mb-3">
                            <div style={{ width: "200px" }}>
                              <input
                                type="text"
                                className="form-control form-control-xs"
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                              />
                            </div>
                          </div>

                          <div className="table-responsive" style={{ maxHeight: "350px" }}>
                            <table className="table table-bordered table-striped table-sm">
                              <thead>
                                <tr className="bg-light">
                                  {/* <th style={{ width: "50px" }}>#</th> */}
                                  <th style={{ width: "150px", position: "sticky", top: 0, zIndex: 1, backgroundColor: "#f8f9fa" }}>Subject Type</th>
                                  <th style={{ width: "150px", position: "sticky", top: 0, zIndex: 1, backgroundColor: "#f8f9fa" }}>Subject Code</th>
                                  <th style={{ position: "sticky", top: 0, zIndex: 1, backgroundColor: "#f8f9fa" }}>Subject Name</th>
                                  <th style={{ width: "150px", position: "sticky", top: 0, zIndex: 1, backgroundColor: "#f8f9fa" }}>Stream</th>
                                  <th style={{ width: "50px", position: "sticky", top: 0, zIndex: 1, backgroundColor: "#f8f9fa" }}>Eligible Student Counts</th>
                                  <th style={{ width: "150px", position: "sticky", top: 0, zIndex: 1, backgroundColor: "#f8f9fa" }}>Enter Date</th>
                                  <th style={{ width: "150px", position: "sticky", top: 0, zIndex: 1, backgroundColor: "#f8f9fa" }}>Time (Shift)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredSubjects.length === 0 ? (
                                  <tr>
                                    <td colSpan="7" className="text-center py-3 text-muted">
                                      {subjects.length === 0 ? "No subjects found" : "No matching subjects found"}
                                    </td>
                                  </tr>
                                ) : (
                                  filteredSubjects.map((subject) => {
                                    const originalIndex = subjects.findIndex(
                                      (sub) => sub.subjectCode === subject.subjectCode && sub.subjectName === subject.subjectName
                                    );
                                    const targetIndex = originalIndex !== -1 ? originalIndex : 0;
                                    return (
                                      <tr key={subject.subjectCode || targetIndex}>
                                        {/* <td>{targetIndex + 1}</td> */}
                                        <td>
                                          {/* <span className="badge bg-dark text-white" style={{ textTransform: "uppercase" }}> */}
                                          {subject.subjectType}
                                          {/* </span> */}
                                        </td>
                                        <td><strong>{subject.subjectCode}</strong></td>
                                        <td>{subject.subjectName}</td>
                                        <td>
                                          {/* <span className="badge bg-secondary text-white" style={{ textTransform: "uppercase" }}> */}
                                          {subject.stream || ""}
                                          {/* </span> */}
                                        </td>
                                        <td style={{ textAlign: "center" }}>
                                          {/* <span className="badge bg-info text-white"> */}
                                          <strong>{subject?.eligibleStudentCount}</strong>
                                          {/* </span> */}
                                        </td>
                                        <td>
                                          <DatePicker
                                            minDate={new Date()}
                                            selected={
                                              subject.examDate
                                                ? (subject.examDate.includes("-")
                                                  ? new Date(subject.examDate.split("-").reverse().join("-"))
                                                  : new Date(subject.examDate))
                                                : null
                                            }
                                            onChange={(date) => {
                                              if (!date) {
                                                handleDateChange(targetIndex, "");
                                                return;
                                              }
                                              const day = String(date.getDate()).padStart(2, "0");
                                              const month = String(date.getMonth() + 1).padStart(2, "0");
                                              const year = date.getFullYear();
                                              handleDateChange(targetIndex, `${day}-${month}-${year}`);
                                            }}
                                            dateFormat="dd-MM-yyyy"
                                            className="form-control form-control-xs"
                                            placeholderText="dd-mm-yyyy"
                                            onKeyDown={(e) => e.preventDefault()}
                                            showYearDropdown
                                            scrollableYearDropdown
                                            yearDropdownItemNumber={10}
                                            isClearable
                                            disabled={loading || isFinalSaved || subject.subjectType === "MATRI_BHASA"}
                                          />
                                        </td>
                                        <td>
                                          <select
                                            className="form-control form-control-xs"
                                            value={subject.examTime}
                                            onChange={(e) => handleTimeChange(targetIndex, e.target.value)}
                                            style={{ color: subject.examTime ? "#000" : "#c9c8c8" }}
                                            disabled={loading || isFinalSaved || subject.subjectType === "MATRI_BHASA"}
                                          >
                                            <option value="" style={{ color: "#c9c8c8" }}>-- Select Time --</option>
                                            {Array.from(new Set([...(timeSlots || []), subject.examTime])).filter(Boolean).map((slot) => (
                                              <option key={slot} value={slot} style={{ color: "#000" }}>
                                                {slot}
                                              </option>
                                            ))}
                                          </select>
                                        </td>
                                      </tr>
                                    );
                                  })
                                )}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                    </div>
                    {isSearched && (
                      <div className="card-footer bg-white d-flex justify-content-between align-items-center py-3">
                        <div>
                          <button
                            type="button"
                            className="btn btn-danger"
                            onClick={handleReset}
                            disabled={loading || isFinalSaved}
                          >
                            <i className="fa fa-refresh"></i>&nbsp;Reset
                          </button>
                        </div>
                        <div className="text-right" style={{ textAlign: "right" }}>
                          <button
                            type="button"
                            className="btn btn-secondary me-2"
                            style={{ marginRight: "10px" }}
                            onClick={() => handleSave("DRAFT")}
                            disabled={loading || isFinalSaved}
                          >
                            <i className="fa fa-save"></i>&nbsp;Draft Save
                          </button>

                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <Footer />
          </div>
        </div>
      </div>
    </>
  );
}

export default SpecialExamSchedule;
